import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import useConsent from "@/hooks/useConsent";

const POSTHOG_TOKEN =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_POSTHOG_KEY) ||
  "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";

/**
 * loadPostHog — attaches the official PostHog snippet to window.
 *
 * The IIFE below is the vendor install snippet, copied verbatim from
 * PostHog's documentation. Its legacy syntax (var / ==) is *required* —
 * PostHog's loader reads into the stub with that exact shape. Do not
 * modernise it; if you need to customise PostHog behaviour, do it on the
 * init call below.
 */
function loadPostHog() {
  if (typeof window === "undefined") return;
  if (window.posthog && typeof window.posthog.capture === "function") {
    try {
      window.posthog.opt_in_capturing();
    } catch (err) {
      if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("PostHog opt_in_capturing failed", err);
      }
    }
    return;
  }
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.crossOrigin = "anonymous"),
          (p.async = !0),
          (p.src =
            s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
            "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
            },
            o = "init capture identify reset opt_in_capturing opt_out_capturing".split(
              " "
            ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */
  window.posthog.init(POSTHOG_TOKEN, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    session_recording: { recordCrossOriginIframes: false, capturePerformance: false },
  });
}

export default function CookieBanner() {
  const { t, lang } = useLang();
  const { status, accept, decline } = useConsent();

  // Load PostHog when consent is already granted (returning visits).
  useEffect(() => {
    if (status === "granted") loadPostHog();
  }, [status]);

  const handleAccept = () => {
    loadPostHog();
    accept();
  };

  if (status !== "pending") return null;

  const isEs = lang === "es";

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label={isEs ? "Aviso de cookies" : "Cookie notice"}
      className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[9998] bg-olive-900 text-cream-100 border border-olive-800 p-5 sm:p-6 shadow-xl"
    >
      <p className="text-[11px] uppercase tracking-[0.28em] text-gold mb-3">
        {isEs ? "Cookies y privacidad" : "Cookies & privacy"}
      </p>
      <p className="font-light text-sm leading-relaxed text-cream-100/85">
        {isEs
          ? "Usamos cookies estrictamente necesarias para operar la tienda y, solo con tu consentimiento, cookies analíticas para entender cómo se usa el sitio. Puedes cambiar de opinión en cualquier momento."
          : "We use strictly necessary cookies to run the store and, only with your consent, analytics cookies to understand how the site is used. You can change your mind at any time."}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleAccept}
          data-testid="cookie-accept"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold-deep text-olive-900 text-[11px] uppercase tracking-[0.26em] hover:bg-gold transition-colors"
        >
          {isEs ? "Aceptar todo" : "Accept all"}
        </button>
        <button
          type="button"
          onClick={decline}
          data-testid="cookie-decline"
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-cream-100/40 text-cream-100 text-[11px] uppercase tracking-[0.26em] hover:border-cream-100"
        >
          {isEs ? "Rechazar" : "Decline"}
        </button>
        <Link
          to="/legal/privacidad"
          className="text-[11px] uppercase tracking-[0.26em] text-cream-100/60 hover:text-gold underline underline-offset-4"
        >
          {isEs ? "Política" : "Policy"}
        </Link>
      </div>
    </div>
  );
}
